import { useState, useEffect } from "react";
import {
  Tag,
  BarChart2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Shield,
  TrendingUp,
  Clock,
  Info,
} from "lucide-react";
import mutualFundService from "@/services/api/mutual-fund.service";

export default function SchemeDetailsPage({ schemeCode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [scheme, setScheme] = useState(null);
  const [returns, setReturns] = useState(null);
  const [navHistory, setNavHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState("returns");

  useEffect(() => {
    fetchSchemeDetails();
  }, [schemeCode]);

  const fetchSchemeDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch scheme details
      const response = await mutualFundService.getSchemeDetails(schemeCode);

      if (response.data.success) {
        setScheme(response.data.data.data);

        // Fetch returns data
        const returnsPromises = [
          mutualFundService.calculateReturns(schemeCode, { period: "1y" }),
          mutualFundService.calculateReturns(schemeCode, { period: "3y" }),
          mutualFundService.calculateReturns(schemeCode, { period: "5y" }),
        ];

        try {
          const returnsResponses = await Promise.all(returnsPromises);

          const returnsData = {
            "1y":
              returnsResponses[0]?.data?.success &&
              returnsResponses[0]?.data?.data?.data
                ? returnsResponses[0].data.data.data
                : null,
            "3y":
              returnsResponses[1]?.data?.success &&
              returnsResponses[1]?.data?.data?.data
                ? returnsResponses[1].data.data.data
                : null,
            "5y":
              returnsResponses[2]?.data?.success &&
              returnsResponses[2]?.data?.data?.data
                ? returnsResponses[2].data.data.data
                : null,
          };

     
          

          setReturns(returnsData);
        } catch (returnsError) {
          console.error("Error fetching returns data:", returnsError);
          // Continue execution even if returns fetch fails
        }

        // Fetch NAV history
        try {
          const navHistoryResponse = await mutualFundService.getNAVHistory(
            schemeCode,
            { limit: 365 }
          );

          if (navHistoryResponse.data.success) {
            setNavHistory(navHistoryResponse.data.data || []);
          }
        } catch (navError) {
          console.error("Error fetching NAV history:", navError);
          // Continue execution even if NAV history fetch fails
        }
      } else {
        throw new Error("Failed to fetch scheme details");
      }
    } catch (error) {
      console.error("Error fetching scheme details:", error);
      setError("Failed to load scheme details. Please try again later.");
      showNotification("Failed to load scheme details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Format numbers with commas
  const formatNumber = (num, decimals = 2) => {
    if (num === undefined || num === null) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  // Calculate historical growth from inception
  const calculateInceptionGrowth = () => {
    if (!scheme?.data || scheme.data.length < 2) return null;
    const firstNav = parseFloat(scheme.data[scheme.data.length - 1].nav);
    const currentNav = parseFloat(scheme.data[0].nav);
    return ((currentNav - firstNav) / firstNav) * 100;
  };

  // Get the latest NAV value
  const getLatestNAV = () => {
    if (!scheme?.data || scheme.data.length === 0) return "N/A";
    return scheme.data[0].nav;
  };

  // Get the NAV date
  const getLatestNAVDate = () => {
    if (!scheme?.data || scheme.data.length === 0) return "N/A";
    return scheme.data[0].date;
  };

  // Determine risk level based on category
  const determineRiskLevel = () => {
    const category = scheme?.meta?.scheme_category?.toLowerCase() || "";

    if (category.includes("equity") || category.includes("small cap")) {
      return {
        level: "High",
        description:
          "This fund has higher volatility but potential for higher returns. Suitable for investors with a long-term horizon who can tolerate significant market fluctuations.",
      };
    } else if (
      category.includes("debt") ||
      category.includes("banking") ||
      category.includes("liquid")
    ) {
      return {
        level: "Low",
        description:
          "This fund focuses on capital preservation with stable returns. Ideal for conservative investors with shorter investment horizons.",
      };
    } else {
      return {
        level: "Moderate",
        description:
          "This fund balances risk and returns with a mix of debt and equity instruments, providing stability while capturing market growth.",
      };
    }
  };

  // Calculate performance metrics - improved to handle missing data
  const calculatePerformanceChange = (period) => {
    // First check if we have returns data and if the specific period exists
    if (!returns || !returns[period]) return null;

    // Get return value - based on your API response format, absoluteReturn is the key to use
    const returnValue = returns[period].absoluteReturn;
    if (returnValue === undefined || returnValue === null || isNaN(returnValue))
      return null;

    return returnValue;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="container mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <div className="flex items-center">
              <AlertTriangle className="text-red-500 mr-2" size={20} />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const riskProfile = determineRiskLevel();
  const inceptionGrowth = calculateInceptionGrowth();
  const growthFromInception =
    inceptionGrowth !== null ? formatNumber(inceptionGrowth) : "N/A";

  // Render the notification component
  const renderNotification = () => {
    if (!notification) return null;

    const bgColor =
      notification.type === "success"
        ? "bg-green-100 border-green-500"
        : notification.type === "error"
        ? "bg-red-100 border-red-500"
        : notification.type === "warning"
        ? "bg-yellow-100 border-yellow-500"
        : "bg-blue-100 border-blue-500";

    return (
      <div
        className={`fixed bottom-4 right-4 p-4 rounded shadow-lg border-l-4 ${bgColor} z-50`}
      >
        <p>{notification.message}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {renderNotification()}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 break-words pr-8">
            {scheme?.meta?.scheme_name || "Fund Details"}
          </h1>
          <div className="flex flex-wrap items-center mt-2 text-sm text-gray-600">
            <div className="flex items-center mr-4 mb-2">
              <Tag size={14} className="mr-1" />
              <span>{scheme?.meta?.fund_house || "N/A"}</span>
            </div>
            <div className="flex items-center mr-4 mb-2">
              <BarChart2 size={14} className="mr-1" />
              <span>{scheme?.meta?.scheme_category || "N/A"}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1">
          {/* Left Column */}
          <div>
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === "overview"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-blue-600"
                  }`}
                >
                  Overview
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === "overview" && (
                  <div>
                    {/* NAV Information */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                          <TrendingUp
                            className="mr-2 text-blue-600"
                            size={20}
                          />
                          Current Value & Performance
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <p className="text-sm text-gray-500 mb-1">
                            Current NAV
                          </p>
                          <p className="font-medium text-lg">
                            ₹ {getLatestNAV()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            As of {getLatestNAVDate()}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <p className="text-sm text-gray-500 mb-1">
                            Growth since inception
                          </p>
                          <p
                            className={`font-medium text-lg ${
                              inceptionGrowth > 0
                                ? "text-green-600"
                                : inceptionGrowth < 0
                                ? "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {growthFromInception}%
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Since{" "}
                            {scheme?.data[scheme.data.length - 1]?.date ||
                              "inception"}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <p className="text-sm text-gray-500 mb-1">
                            Risk Level
                          </p>
                          <p className="font-medium text-lg">
                            {riskProfile.level}
                          </p>
                          <div className="flex items-center mt-1">
                            <Shield
                              size={14}
                              className={`mr-1 ${
                                riskProfile.level === "High"
                                  ? "text-red-500"
                                  : riskProfile.level === "Low"
                                  ? "text-green-500"
                                  : "text-yellow-500"
                              }`}
                            />
                            <p className="text-xs text-gray-500">
                              {riskProfile.level} volatility
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fund Information */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                          Fund Information
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">
                            Fund House
                          </p>
                          <p className="font-medium">
                            {scheme?.meta?.fund_house || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">Category</p>
                          <p className="font-medium">
                            {scheme?.meta?.scheme_category || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">Type</p>
                          <p className="font-medium">
                            {scheme?.meta?.scheme_type || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">
                            Scheme Code
                          </p>
                          <p className="font-medium">
                            {scheme?.meta?.scheme_code || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">
                            ISIN (Growth)
                          </p>
                          <p className="font-medium">
                            {scheme?.meta?.isin_growth || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500 mb-1">
                            ISIN (Div Reinvestment)
                          </p>
                          <p className="font-medium">
                            {scheme?.meta?.isin_div_reinvestment || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Returns Section */}
                    <div className="mb-6">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection("returns")}
                      >
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                          <TrendingUp
                            className="mr-2 text-blue-600"
                            size={20}
                          />
                          Historical Returns
                        </h2>
                        {expandedSection === "returns" ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                      {expandedSection === "returns" && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">
                              1 Year Return
                            </p>
                            <p
                              className={`font-medium text-lg ${
                                calculatePerformanceChange("1y") > 0
                                  ? "text-green-600"
                                  : calculatePerformanceChange("1y") < 0
                                  ? "text-red-600"
                                  : "text-gray-600"
                              }`}
                            >
                              {calculatePerformanceChange("1y") !== null
                                ? `${formatNumber(
                                    calculatePerformanceChange("1y")
                                  )}%`
                                : "N/A"}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">
                              3 Year Return
                            </p>
                            <p
                              className={`font-medium text-lg ${
                                calculatePerformanceChange("3y") > 0
                                  ? "text-green-600"
                                  : calculatePerformanceChange("3y") < 0
                                  ? "text-red-600"
                                  : "text-gray-600"
                              }`}
                            >
                              {calculatePerformanceChange("3y") !== null
                                ? `${formatNumber(
                                    calculatePerformanceChange("3y")
                                  )}%`
                                : "N/A"}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">
                              5 Year Return
                            </p>
                            <p
                              className={`font-medium text-lg ${
                                calculatePerformanceChange("5y") > 0
                                  ? "text-green-600"
                                  : calculatePerformanceChange("5y") < 0
                                  ? "text-red-600"
                                  : "text-gray-600"
                              }`}
                            >
                              {calculatePerformanceChange("5y") !== null
                                ? `${formatNumber(
                                    calculatePerformanceChange("5y")
                                  )}%`
                                : "N/A"}
                            </p>
                          </div>

                          {/* Add a message if 5y data is not available */}
                          {calculatePerformanceChange("5y") === null && (
                            <div className="col-span-3 mt-2">
                              <p className="text-xs text-gray-500">
                                Note: 5-year return data may not be available if
                                the fund is less than 5 years old or due to data
                                limitations.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Risk Profile */}
                    <div className="mb-6">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection("risk")}
                      >
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                          <Shield className="mr-2 text-blue-600" size={20} />
                          Risk Profile
                        </h2>
                        {expandedSection === "risk" ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                      {expandedSection === "risk" && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-3">
                            <Shield
                              className={`mr-2 ${
                                riskProfile.level === "High"
                                  ? "text-red-500"
                                  : riskProfile.level === "Low"
                                  ? "text-green-500"
                                  : "text-yellow-500"
                              }`}
                              size={20}
                            />
                            <h3 className="font-medium text-gray-700">
                              Risk Level: {riskProfile.level}
                            </h3>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">
                            {riskProfile.description}
                          </p>

                          <div className="mt-4 bg-white p-3 rounded border border-gray-200">
                            <div className="flex items-center mb-2">
                              <Info size={16} className="text-blue-500 mr-2" />
                              <h4 className="font-medium text-sm">
                                Investor Suitability
                              </h4>
                            </div>
                            <p className="text-xs text-gray-600">
                              {riskProfile.level === "High"
                                ? "Best for investors with a long-term investment horizon (5+ years) who can withstand market volatility."
                                : riskProfile.level === "Low"
                                ? "Ideal for conservative investors with short to medium-term goals who prioritize capital preservation."
                                : "Suitable for investors with a medium-term outlook (3-5 years) seeking a balance of growth and stability."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Fund History Snapshot */}
                    <div className="mb-6">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection("history")}
                      >
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                          <Clock className="mr-2 text-blue-600" size={20} />
                          Fund History
                        </h2>
                        {expandedSection === "history" ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                      {expandedSection === "history" && (
                        <div className="mt-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-sm font-medium">NAV History</p>
                              <p className="text-xs text-gray-500">
                                {scheme?.data?.length || 0} data points
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              <p className="text-xs text-gray-500 mt-1">
                                Since{" "}
                                {scheme?.data[scheme.data.length - 1]?.date ||
                                  "inception"}
                              </p>

                              {/* First recorded NAV section */}
                              <p className="text-xs text-gray-500">
                                First recorded NAV
                              </p>
                              <p className="font-medium">
                                ₹
                                {scheme?.data?.[scheme.data.length - 1]?.nav ||
                                  "N/A"}
                                <span className="text-xs text-gray-500 ml-2">
                                  (
                                  {scheme?.data?.[scheme.data.length - 1]
                                    ?.date || "N/A"}
                                  )
                                </span>
                              </p>

                              {/* Latest NAV section */}
                              <p className="text-xs text-gray-500">
                                Latest NAV
                              </p>
                              <p className="font-medium">
                                ₹{scheme?.data?.[0]?.nav || "N/A"}
                                <span className="text-xs text-gray-500 ml-2">
                                  ({scheme?.data?.[0]?.date || "N/A"})
                                </span>
                              </p>

                              <div className="col-span-2 mt-3">
                                <p className="text-xs text-gray-500 mb-1">
                                  Total Growth
                                </p>
                                <p
                                  className={`font-medium ${
                                    inceptionGrowth > 0
                                      ? "text-green-600"
                                      : inceptionGrowth < 0
                                      ? "text-red-600"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {growthFromInception}% since inception
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
